'use client'

import * as React from 'react'

import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

import { Text } from '@/components/atoms/Text'
import { cn } from '@/lib/utils'

interface CopyButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  value: string
  label?: string
  copiedLabel?: string
  onCopied?: () => void
  animated?: boolean
  feedbackDuration?: number
  copyIcon?: React.ReactNode
  checkIcon?: React.ReactNode
}

const defaultCopyIcon = (
  <span className='material-symbols-rounded leading-none text-[inherit]'>
    content_copy
  </span>
)

const defaultCheckIcon = (
  <span className='material-symbols-rounded leading-none text-[inherit]'>
    check
  </span>
)

const transition = {
  duration: 0.15,
  ease: [0.25, 0.46, 0.45, 0.94] as const
}
const initial = { opacity: 0, scale: 0.6, filter: 'blur(2px)' }
const animate = { opacity: 1, scale: 1, filter: 'blur(0px)' }

function CopyButton({
  value,
  label,
  copiedLabel = 'Copied',
  onCopied,
  animated = true,
  feedbackDuration = 2000,
  copyIcon = defaultCopyIcon,
  checkIcon = defaultCheckIcon,
  className,
  ...props
}: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = React.useState(false)
  const shouldReduceMotion = useReducedMotion()
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = React.useCallback(() => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    onCopied?.()

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setCopied(false), feedbackDuration)
  }, [value, onCopied, feedbackDuration])

  React.useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    },
    []
  )

  const skipAnimation = !animated || shouldReduceMotion
  const activeIcon = copied ? checkIcon : copyIcon
  const activeLabel = copied ? copiedLabel : label

  return (
    <button
      type='button'
      aria-label={copied ? 'Copied' : 'Copy to clipboard'}
      data-slot='copy-button'
      data-copied={copied}
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center gap-1.5',
        className
      )}
      onClick={handleCopy}
      {...props}
    >
      {skipAnimation ? (
        <>
          {activeIcon}
          {activeLabel && <Text level='span'>{activeLabel}</Text>}
        </>
      ) : (
        <AnimatePresence mode='wait' initial={false}>
          <motion.span
            key={copied ? 'check' : 'copy'}
            className='inline-flex items-center justify-center gap-1.5'
            initial={initial}
            animate={animate}
            exit={initial}
            transition={transition}
          >
            {activeIcon}
            {activeLabel && <Text level='span'>{activeLabel}</Text>}
          </motion.span>
        </AnimatePresence>
      )}
    </button>
  )
}

export { CopyButton, type CopyButtonProps }
